import type { ProjectAttentionDetail } from "../api/types.js";

/** Client-communicated operational signal — not UTURA OS-detected. */
export interface ProjectAttention {
  readonly signal: "ok" | "attention" | "neutral" | "risk";
  readonly title: string;
  readonly origin: string;
  readonly quote: string;
  readonly detail: string;
  readonly suggestedAction: string;
}

export type AttentionSource = ProjectAttentionDetail["source"];
export type AttentionStatus = ProjectAttentionDetail["status"];

/** Safe Mode — single map for attention source labels. Never auto-detected wording. */
const ATTENTION_SOURCE_LABELS: Readonly<Record<AttentionSource, string>> = {
  CUSTOMER_REPORTED: "Comunicado pelo cliente",
  OPERATOR_RECORDED: "Registado pela agência",
};

export const formatAttentionSource = (source: AttentionSource): string =>
  ATTENTION_SOURCE_LABELS[source];

const AUTO_DETECTED_LABEL = "Detetado pelo UTURA OS";

/** First open attention wins — Project Workspace shows one at a time. */
export const resolveOpenProjectAttention = (
  attentions: readonly ProjectAttentionDetail[],
): ProjectAttention | null => {
  const open = attentions.find((attention) => attention.status === "OPEN");
  if (open === undefined) {
    return null;
  }
  return mapAttentionToProjectView(open);
};

export const mapAttentionToProjectView = (attention: ProjectAttentionDetail): ProjectAttention => ({
  signal: attention.status === "OPEN" ? "attention" : "ok",
  title: attention.title,
  origin: formatAttentionSource(attention.source),
  quote: attention.customerQuote,
  detail: attention.description,
  suggestedAction: attention.suggestedAction ?? "Rever comunicação do cliente",
});

/** Guardrail test helper — CUSTOMER_REPORTED must never read as UTURA OS-detected. */
export const attentionOriginIsSafe = (origin: string): boolean =>
  !origin.includes(AUTO_DETECTED_LABEL);
